"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { getKvStoreCompletions } from "#src/datasource/kvstore_completions.js";
import {
  AutoDetectRegistry,
  autoDetectFormat
} from "#src/kvstore/auto_detect.js";
import {
  extractQueryAndFragment,
  finalPipelineUrlComponent,
  parsePipelineUrlComponent,
  splitPipelineUrl
} from "#src/kvstore/url.js";
import {
  applyCompletionOffset,
  emptyCompletionResult,
  getPrefixMatchesWithDescriptions
} from "#src/util/completion.js";
import { RefCounted } from "#src/util/disposable.js";
export function findSourceGroupBasedOnSeparator(path, separator) {
  if (separator === void 0) {
    if (path.indexOf("/") === -1) {
      separator = ":";
    } else {
      separator = "/";
    }
  }
  const index = path.lastIndexOf(separator);
  if (index === -1) {
    return 0;
  }
  return index + 1;
}
export function suggestLayerNameBasedOnSeparator(path, separator) {
  const groupIndex = findSourceGroupBasedOnSeparator(path, separator);
  return path.substring(groupIndex);
}
export function makeEmptyDataSourceSpecification() {
  return {
    url: "",
    transform: void 0,
    enableDefaultSubsources: true,
    subsources: /* @__PURE__ */ new Map()
  };
}
const schemePattern = /^(?:([a-zA-Z][a-zA-Z0-9-+_]*):\/\/)?(.*)$/;
export class DataSourceRegistry extends RefCounted {
  constructor(sharedKvStoreContext) {
    super();
    this.sharedKvStoreContext = sharedKvStoreContext;
  }
  get credentialsManager() {
    return this.sharedKvStoreContext.credentialsManager;
  }
  get chunkManager() {
    return this.sharedKvStoreContext.chunkManager;
  }
  dataSources = /* @__PURE__ */ new Map();
  kvStoreBasedDataSources = /* @__PURE__ */ new Map();
  autoDetectRegistry = new AutoDetectRegistry();
  register(provider) {
    this.dataSources.set(provider.scheme, provider);
  }
  registerKvStoreBasedProvider(provider) {
    this.kvStoreBasedDataSources.set(provider.scheme, provider);
  }
  getProvider(url) {
    const m = url.match(schemePattern);
    if (m === null || m[1] === void 0) {
      throw new Error(
        `Data source URL must have the form "<scheme>://<path>".`
      );
    }
    const [, providerScheme, providerUrl] = m;
    const factory = this.dataSources.get(providerScheme);
    if (factory === void 0) {
      throw new Error(
        `Unsupported data source: ${JSON.stringify(providerScheme)}.`
      );
    }
    return [factory, providerUrl, providerScheme];
  }
  async autoDetectFormat(options) {
    const { matches, url } = await autoDetectFormat({
      url: options.url,
      kvStoreContext: this.sharedKvStoreContext.kvStoreContext,
      signal: options.signal,
      progressListener: options.progressListener,
      autoDetectDirectory: () => this.autoDetectRegistry.directorySpec,
      autoDetectFile: () => this.autoDetectRegistry.fileSpec
    });
    if (matches.length !== 1) {
      let message;
      if (matches.length === 0) {
        message = "no format detected";
      } else {
        message = `multiple formats detected: ${JSON.stringify(matches)}`;
      }
      throw new Error(
        `Failed to auto-detect data source for ${JSON.stringify(options.url)}: ${message}`
      );
    }
    return `${url}|${matches[0].suffix}`;
  }
  async resolveKvStoreBasedDataSource(options) {
    while (true) {
      const finalPart = parsePipelineUrlComponent(
        finalPipelineUrlComponent(options.url)
      );
      const dataSourceProvider = this.kvStoreBasedDataSources.get(
        finalPart.scheme
      );
      if (dataSourceProvider === void 0) {
        const newUrl = await this.autoDetectFormat(options);
        options = { ...options, url: newUrl };
        continue;
      }
      return await dataSourceProvider.get({
        registry: this,
        url: finalPart,
        kvStoreUrl: options.url.substring(
          0,
          options.url.length - finalPart.url.length - 1
        ),
        signal: options.signal,
        progressListener: options.progressListener,
        state: options.state
      });
    }
  }
  async resolvePipeline(options) {
    const pipelineParts = splitPipelineUrl(options.url);
    const basePart = pipelineParts[0];
    const baseScheme = basePart.scheme;
    {
      const provider = this.sharedKvStoreContext.kvStoreContext.baseKvStoreProviders.get(
        baseScheme
      );
      if (provider !== void 0) {
        return await this.resolveKvStoreBasedDataSource(options);
      }
    }
    if (pipelineParts.length !== 1) {
      throw new Error(`${baseScheme}: scheme does not support | URL pipelines`);
    }
    const suffix = basePart.suffix ?? "";
    if (!suffix.startsWith("//")) {
      throw new Error(`${baseScheme}: URLs must start with "${baseScheme}://"`);
    }
    const providerUrl = suffix.substring(2);
    {
      const provider = this.dataSources.get(baseScheme);
      if (provider !== void 0) {
        return await provider.get({
          ...options,
          url: pipelineParts[0].url,
          providerScheme: baseScheme,
          providerUrl,
          registry: this,
          signal: options.signal ?? new AbortController().signal
        });
      }
    }
    throw new Error(`Unsupported scheme: ${baseScheme}:`);
  }
  async get(options) {
    const redirectLog = /* @__PURE__ */ new Set();
    let url = options.url;
    url = url.trim();
    url = url.replace(/\|+$/, "");
    let originalCanonicalUrl;
    while (true) {
      redirectLog.add(url);
      const dataSource = await this.resolvePipeline({ ...options, url });
      if (originalCanonicalUrl === void 0) {
        originalCanonicalUrl = dataSource.canonicalUrl;
      }
      if ("targetUrl" in dataSource) {
        const { targetUrl } = dataSource;
        if (redirectLog.has(targetUrl)) {
          throw Error(
            `Layer source redirection contains loop: ${JSON.stringify([
              ...redirectLog,
              targetUrl
            ])}`
          );
        }
        if (redirectLog.size >= 10) {
          throw Error(
            `Too many layer source redirections: ${JSON.stringify([
              ...redirectLog,
              targetUrl
            ])}`
          );
        }
        url = targetUrl;
        continue;
      }
      return { ...dataSource, redirectLog, originalCanonicalUrl };
    }
  }
  // Converts legacy precomputed mesh and skeleton datasource URLs.
  convertLegacyUrl(options) {
    try {
      const [provider, providerUrl, providerScheme] = this.getProvider(
        options.url
      );
      if (provider.convertLegacyUrl === void 0) return options.url;
      return provider.convertLegacyUrl({
        ...options,
        providerUrl,
        providerScheme,
        registry: this
      });
    } catch {
      return options.url;
    }
  }
  async completeUrl(options) {
    const { signal } = options;
    const { url } = options;
    const finalComponent = finalPipelineUrlComponent(url);
    const parsedFinalComponent = parsePipelineUrlComponent(finalComponent);
    const { scheme } = parsedFinalComponent;
    if (finalComponent === url && !(parsedFinalComponent.suffix ?? "").startsWith("//")) {
      const providers = [];
      const add = (iterable, predicate) => {
        for (const provider of iterable) {
          if (predicate?.(provider) === false) continue;
          providers.push(provider);
        }
      };
      if (finalComponent === url) {
        add(
          this.sharedKvStoreContext.kvStoreContext.baseKvStoreProviders.values()
        );
        add(this.dataSources.values(), (provider) => provider.hidden !== true);
      } else {
        add(this.kvStoreBasedDataSources.values());
        add(
          this.sharedKvStoreContext.kvStoreContext.kvStoreAdapterProviders.values()
        );
      }
      const schemeSuffix = finalComponent === url ? "//" : "";
      return {
        offset: url.length - finalComponent.length,
        completions: getPrefixMatchesWithDescriptions(
          scheme,
          providers,
          ({ scheme: scheme2 }) => `${scheme2}:${schemeSuffix}`,
          ({ description }) => description
        )
      };
    }
    if (parsedFinalComponent.suffix === void 0) {
      const prevPipelineUrl = options.url.substring(
        0,
        options.url.length - finalComponent.length - 1
      );
      const { matches } = await autoDetectFormat({
        url: prevPipelineUrl,
        kvStoreContext: this.sharedKvStoreContext.kvStoreContext,
        signal: options.signal,
        progressListener: options.progressListener,
        autoDetectDirectory: () => this.autoDetectRegistry.directorySpec,
        autoDetectFile: () => this.autoDetectRegistry.fileSpec
      });
      if (matches.length === 0) {
        throw new Error(
          `Failed to auto-detect data source for ${JSON.stringify(prevPipelineUrl)}`
        );
      }
      return {
        offset: url.length - finalComponent.length,
        completions: getPrefixMatchesWithDescriptions(
          parsedFinalComponent.scheme,
          matches,
          ({ suffix }) => suffix,
          ({ description }) => description
        )
      };
    }
    if (finalComponent === url) {
      const provider = this.dataSources.get(scheme);
      if (provider !== void 0) {
        if (provider.completeUrl === void 0) return emptyCompletionResult;
        const completions = await provider.completeUrl({
          registry: this,
          url: options.url,
          providerUrl: parsedFinalComponent.suffix.substring(2),
          signal: signal ?? new AbortController().signal,
          progressListener: options.progressListener
        });
        return applyCompletionOffset(scheme.length + 3, completions);
      }
    }
    {
      const provider = this.kvStoreBasedDataSources.get(scheme);
      if (provider !== void 0) {
        if (provider.completeUrl === void 0) return emptyCompletionResult;
        const completions = await provider.completeUrl({
          registry: this,
          signal: signal ?? new AbortController().signal,
          progressListener: options.progressListener,
          kvStoreUrl: url.substring(0, url.length - finalComponent.length - 1),
          url: parsedFinalComponent
        });
        return applyCompletionOffset(
          url.length - finalComponent.length + parsedFinalComponent.scheme.length + 1,
          completions
        );
      }
    }
    return await getKvStoreCompletions(this.sharedKvStoreContext, {
      url,
      signal,
      progressListener: options.progressListener,
      autoDetectDirectory: () => this.autoDetectRegistry.directorySpec
    });
  }
  suggestLayerName(url) {
    const parts = splitPipelineUrl(url);
    for (let i = parts.length - 1; i >= 0; --i) {
      let { suffix } = parts[i];
      if (!suffix) continue;
      suffix = suffix.replace(/^\/+/, "").replace(/\/+$/, "");
      if (!suffix) continue;
      return suggestLayerNameBasedOnSeparator(suffix);
    }
    return void 0;
  }
}
export class KvStoreBasedDataSourceLegacyUrlAdapter {
  constructor(base, scheme = base.scheme) {
    this.base = base;
    this.scheme = scheme;
  }
  get hidden() {
    return true;
  }
  get description() {
    return this.base.description;
  }
  parseProviderUrl(url) {
    if (url.includes("|")) {
      throw new Error("Only a single pipeline component supported");
    }
    const { base, queryAndFragment } = extractQueryAndFragment(url);
    return {
      kvStoreUrl: base,
      queryAndFragment,
      url: parsePipelineUrlComponent(`${this.base.scheme}:${queryAndFragment}`)
    };
  }
  get(options) {
    const { kvStoreUrl, url } = this.parseProviderUrl(options.providerUrl);
    return this.base.get({
      registry: options.registry,
      url,
      kvStoreUrl,
      state: options.state,
      signal: options.signal,
      progressListener: options.progressListener
    });
  }
  async completeUrl(options) {
    const { kvStoreUrl, url, queryAndFragment } = this.parseProviderUrl(
      options.providerUrl
    );
    if (queryAndFragment === "") {
      return await getKvStoreCompletions(
        options.registry.sharedKvStoreContext,
        {
          url: options.providerUrl,
          signal: options.signal,
          progressListener: options.progressListener,
          singlePipelineComponent: true,
          directoryOnly: this.base.expectsDirectory
        }
      );
    }
    if (!this.base.completeUrl) return emptyCompletionResult;
    return this.base.completeUrl({
      registry: options.registry,
      signal: options.signal,
      progressListener: options.progressListener,
      kvStoreUrl,
      url
    });
  }
}
